import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MoreVertical, Calendar, MessageCircle, Bell, Search, User, Eye, Loader2, X, MapPin, Clock } from 'lucide-react';
import { useGetHellocianProjects } from '../../services/hellocian.order.service';
import NotificationBadge from '../NotificationBadge';
import ThemeToggle from '../ThemeToggle';
import { useAuthStore } from '../../store/authStore';

const Overview = () => {
    const { user } = useAuthStore();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('ongoing');
  const [showProjectMenu, setShowProjectMenu] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCountdownModal, setShowCountdownModal] = useState(null);
  const [showProjectDetails, setShowProjectDetails] = useState(null);
  const [historyStatusFilter, setHistoryStatusFilter] = useState('all');

  const { data: allProjects = [], isLoading } = useGetHellocianProjects();


  const ongoing = allProjects.filter(p => p.status === 'in-progress');
  const history = allProjects.filter(p => ['completed', 'cancelled', 'disputed'].includes(p.status));

  // Handle URL routing
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab && ['ongoing', 'history'].includes(tab)) {
      setActiveTab(tab);
    }
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const url = new URL(window.location);
    url.searchParams.set('tab', tab);
    window.history.pushState({}, '', url);
  };



 

  const getOngoingMenuItems = (project) => [
    { label: 'View Project', action: () => setShowProjectDetails(project) },
  ];

  const getHistoryMenuItems = (project) => [
    {label: 'View Details', action: () => setShowProjectDetails(project) },
  ];

  const OngoingProjectCard = ({ project }) => (
    <div className="bg-white dark:bg-[#1d2026] rounded-lg p-4 shadow-sm border border-gray-200 dark:border-[#32353c] mb-4 transition-all hover:shadow-md">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <span className="text-[#174568] text-xs font-normal uppercase tracking-wider">{project.gigCategory?.join(', ') || 'Service'}</span>
          <h3 className="text-[13px] md:text-[15px] text-gray-900 dark:text-white font-normal md:font-medium mt-1 mb-2 line-clamp-1">{project.title}</h3>
        </div>
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowProjectMenu(showProjectMenu === project._id ? null : project._id);
            }}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <MoreVertical size={16} className="text-gray-400 dark:text-gray-500" />
          </button>
          
          {showProjectMenu === project._id && (
            <div className="absolute right-0 top-10 bg-white dark:bg-[#1d2026] border border-gray-200 dark:border-[#32353c] rounded-lg shadow-xl py-2 z-50 min-w-[160px]">
              {getOngoingMenuItems(project).map((item, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    item.action();
                    setShowProjectMenu(null);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left ${
                    item.danger ? 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30' : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
   
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <div className="mb-3 relative group">
        <img 
          src={project.img || (project.gigId?.cover)} 
          alt={project.title}
          className="w-full h-36 object-cover rounded-lg shadow-inner bg-gray-50 dark:bg-gray-700"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors rounded-lg"></div>
      </div>
      
      <div className="flex items-center justify-between">
        <p className="text-[#174568] dark:text-blue-400 font-bold text-lg">${project.price.toFixed(2)}</p>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setShowCountdownModal(project)}
            className="p-2 hover:bg-blue-50 dark:hover:bg-blue-900/40 text-gray-500 dark:text-gray-400 hover:text-[#174568] dark:hover:text-blue-300 rounded-xl transition-all border border-transparent hover:border-blue-100 dark:hover:border-blue-800"
          >
            <Calendar size={18} />
          </button>
         
           <button 
            onClick={() => {
              const cid = project.chatId?._id || project.chatId;
              if (cid) navigate(`/hellocians/messages/${cid}`);
            }}
            className="p-2 hover:bg-blue-50 dark:hover:bg-blue-900/40 text-gray-500 dark:text-gray-400 hover:text-[#174568] dark:hover:text-blue-300 rounded-xl transition-all border border-transparent hover:border-blue-100 dark:hover:border-blue-800"
          >
            <MessageCircle size={18} />
          </button>
        </div>
      </div>
    </div>
  );

  const HistoryProjectCard = ({ project }) => (
    <div className="bg-white dark:bg-[#1d2026] rounded-lg p-5 shadow-sm border border-gray-100 dark:border-[#32353c] mb-4 hover:shadow-md transition-all group">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[#174568] text-[10px] font-bold uppercase tracking-widest">{project.gigCategory?.join(', ') || 'Service'}</span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
              project.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 
              project.status === 'cancelled' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'
            }`}>
              {project.status}
            </span>
          </div>
          <h3 className="text-[13px] md:text-[15px] text-gray-900 dark:text-white font-normal md:font-medium group-hover:text-[#174568] transition-colors">{project.title}</h3>
          <p className="text-gray-900 dark:text-white font-medium font-semibold text-[16px] md:text-xl mt-3">${project.price.toFixed(2)}</p>
        </div>
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowProjectMenu(showProjectMenu === project._id ? null : project._id);
            }}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <MoreVertical size={16} className="text-gray-400 dark:text-gray-500" />
          </button>
          
          {showProjectMenu === project._id && (
            <div className="absolute right-0 top-10 bg-white dark:bg-[#1d2026] border border-gray-200 dark:border-[#32353c] rounded-lg shadow-xl py-2 z-50 min-w-[160px]">
              {getHistoryMenuItems(project).map((item, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    item.action();
                    setShowProjectMenu(null);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left text-gray-700 dark:text-gray-300"
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-4 mt-4 text-gray-400 dark:text-gray-500 border-t border-gray-50 dark:border-[#32353c] pt-3">
         <div className="flex items-center gap-1.5 text-xs font-medium">
            <Calendar size={14} />
            <span>{project.deliveredAt || project.updatedAt ? new Date(project.deliveredAt || project.updatedAt).toLocaleDateString() : 'N/A'}</span>
         </div>

         <Link 
            to={`/hellocians/messages/${project.chatId?._id || project.chatId}`} 
            className="flex items-center gap-1.5 text-xs font-medium hover:text-[#174568] transition-colors"
         >
            <MessageCircle size={14} />
            <span>Chat</span>
         </Link>

      </div>
    </div>
  );

  const CountdownModal = ({ project, onClose }) => {
    const [timeLeft, setTimeLeft] = useState('');

    useEffect(() => {
        const timer = setInterval(() => {
            const now = new Date();
            const end = new Date(project.deliveryDate);
            const diff = end - now;

            if (diff <= 0) {
                setTimeLeft('Deadline passed');
                clearInterval(timer);
                return;
            }

            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
            const minutes = Math.floor((diff / 1000 / 60) % 60);
            const seconds = Math.floor((diff / 1000) % 60);

            setTimeLeft(`${days}d ${hours}h ${minutes}m ${seconds}s`);
        }, 1000);

        return () => clearInterval(timer);
    }, [project.deliveryDate]);

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 dark:bg-black/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-[#1d2026] rounded-3xl p-8 max-w-sm w-full shadow-2xl animate-in zoom-in-95 duration-200 text-center">
                <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/30 text-[#174568] dark:text-blue-400 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Calendar size={32} />
                </div>
                <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">Project Deadline</h3>
                <p className="text-gray-500 dark:text-gray-400 text-[13px] mb-4 px-4">Remaining time to deliver the project for <span className="font-medium text-gray-600 dark:text-gray-300">"{project.title}"</span></p>
                
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 mb-4 border border-gray-100 dark:border-[#32353c]">
                    <span className="text-[18px] font-medium text-[#174568] dark:text-blue-400 tabular-nums tracking-tight">
                        {timeLeft || 'Calculating...'}
                    </span>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <button 
                        onClick={onClose}
                        className="py-1.5 px-3 rounded-md text-[12px] font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                        Close
                    </button>
                    <button 
                        onClick={() => {
                            onClose();
                            setShowProjectDetails(project);
                        }}
                        className="py-1.5 px-3 rounded-md font-medium text-[12px] bg-[#174568] text-white hover:bg-gray-900 transition-colors shadow-lg shadow-blue-900/10"
                    >
                        Project Details
                    </button>
                </div>
            </div>
        </div>
    );
  };

  const filteredOngoingProjects = ongoing.filter(project =>
    project.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredHistoryProjects = history.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = historyStatusFilter === 'all' || project.status === historyStatusFilter;
    return matchesSearch && matchesStatus;
  });

  const ProjectDetailsModal = ({ project, onClose }) => {
    if (!project) return null;

    const statusColors = {
      'pending': 'bg-orange-100 text-orange-700',
      'in-progress': 'bg-blue-100 text-blue-700',
      'completed': 'bg-green-100 text-green-700',
      'cancelled': 'bg-red-100 text-red-700',
      'disputed': 'bg-red-100 text-red-700'
    };

    return (
      <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/40 dark:bg-black/60 backdrop-blur-sm">
        <div className="bg-white dark:bg-[#1d2026] rounded-xl max-w-lg w-full shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100 dark:border-[#32353c] flex items-center justify-between bg-gray-50/50 dark:bg-[#1d2026]/50">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600 flex items-center justify-center text-[#174568] dark:text-blue-400">
                <Eye size={17} />
              </div>
              <div>
                <h3 className="text-[16px] font-medium text-gray-900 dark:text-white leading-tight">Project Details</h3>
                <p className="text-xs text-gray-500 font-mono">#{project._id.substring(project._id.length - 8).toUpperCase()}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-400 dark:text-gray-500">
              <X size={20} />
            </button>
          </div>

          <div className="overflow-y-auto p-3 space-y-3">
            {/* Project Banner */}
            <div className="relative rounded-md overflow-hidden h-40 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600">
                <img 
                    src={project.img || project.gigId?.cover} 
                    alt="" 
                    className="w-full h-full object-cover"
                />
                <div className="absolute top-3 left-3">
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-normal capitalize tracking-wider shadow-sm ${statusColors[project.status] || 'bg-gray-100 text-gray-700'}`}>
                        {project.status}
                    </span>
                </div>
            </div>

            {/* Content Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-4">
                <div>
                   <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Project Title</label>
                   <h4 className="text-gray-900 dark:text-white text-[14px] font-medium leading-snug">{project.title}</h4>
                </div>
                <div>
                   <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Category</label>
                   <p className="text-gray-700 dark:text-gray-300 text-[14px] font-medium">{project.gigCategory?.join(', ') || 'N/A'}</p>
                </div>
                <div>
                   <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Pricing Package</label>
                   <p className="text-gray-700 dark:text-gray-300 text-[14px] font-medium capitalize">{project.pricingPackage}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                   <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Deadline</label>
                   <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                      <Calendar size={14} className="text-gray-400" />
                      <span className="text-[14px] font-medium">{new Date(project.deliveryDate).toLocaleDateString()}</span>
                   </div>
                </div>
                <div>
                   <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Budget</label>
                   <p className="text-xl font-semibold text-[#174568]">${project.price.toFixed(2)}</p>
                </div>
                <div>
                   <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Client</label>
                   <div className="flex items-center gap-2">
                       <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center text-[10px] font-bold text-gray-500">
                           {project.clientId?.firstName?.charAt(0) || 'C'}
                       </div>
                       <p className="text-gray-700 dark:text-gray-300 text-sm font-medium">{project.clientId?.firstName} {project.clientId?.lastName}</p>
                   </div>
                   <div className="mt-1 flex flex-col gap-0.5 pl-8">
                       {project.clientId?.country && (
                           <p className="text-[11px] text-gray-500 font-medium flex items-center gap-1">
                               <  MapPin size={14} className="text-gray-400" /> {project.clientId.country}
                           </p>
                       )}
                       {project.clientId?.timeZone && (
                           <p className="text-[11px] text-gray-500 font-medium flex items-center gap-1">
                               <Clock size={14} className="text-gray-400" /> {project.clientId.timeZone}
                           </p>
                       )}
                   </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 bg-gray-50 dark:bg-[#1d2026]/50 border-t border-gray-100 dark:border-[#32353c] flex gap-3">
            <button 
                onClick={onClose}
                className="flex-1 py-3 px-4 rounded-xl text-sm font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
                Close
            </button>
            <button 
                onClick={() => {
                   const cid = project.chatId?._id || project.chatId;
                   if (cid) navigate(`/hellocians/messages/${cid}`);
                   onClose();
                }}
                className="flex-1 py-3 px-4 rounded-xl text-sm font-bold bg-[#174568] text-white hover:bg-gray-900 transition-all shadow-lg shadow-blue-900/10 flex items-center justify-center gap-2"
            >
                <MessageCircle size={16} />
                Open Chat
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setShowProjectMenu(null);
        setShowNotifications(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0A0A0A]">
      {/* Header */}
      <div className="bg-white dark:bg-[#141414] border-b border-gray-200 dark:border-gray-800 px-4 md:px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Tab Navigation */}
          <div className="flex items-center gap-4 md:gap-8">
            <button
              onClick={() => handleTabChange('ongoing')}
              className={`flex items-center gap-2 pb-4 border-b-2 transition-colors ${
                activeTab === 'ongoing'
                  ? 'border-[#174568] dark:border-blue-400 text-[#174568] dark:text-blue-400 font-semibold'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              Ongoing
              <span className="bg-[#174568] text-white text-[10px] font-semibold rounded-full px-1.5 py-0.5 min-w-[20px] h-5 flex items-center justify-center">
                {ongoing.length}
              </span>
            </button>
            
            <button
              onClick={() => handleTabChange('history')}
              className={`flex items-center gap-2 pb-4 border-b-2 transition-colors ${
                activeTab === 'history'
                  ? 'border-[#174568] dark:border-blue-400 text-[#174568] dark:text-blue-400 font-semibold'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              History
              <span className="bg-gray-400 text-white text-[10px] font-semibold rounded-full px-1.5 py-0.5 min-w-[20px] h-5 flex items-center justify-center">
                {history.length}
              </span>
            </button>

          </div>

          <div className="flex items-center gap-4">
            <NotificationBadge />

               <ThemeToggle />
            
               <div className="w-7 h-7 md:w-10 md:h-10 rounded-full border-[1px] border-gray-200 dark:border-[#32353c] bg-gray-200 dark:bg-gray-600 overflow-hidden">
          <img 
  src={user?.profilePicture || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face"} 
  alt="User" 
  className="w-full h-full object-cover" 
/>
        </div>    
            <div className="relative hidden md:block">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-[#32353c] bg-transparent dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col md:flex-row h-[calc(100vh-80px)]">
        {/* Left Sidebar - Changes based on active tab */}
        <div className="w-full md:w-80 flex-1 md:flex-none bg-gray-100 dark:bg-[#0b0e14] p-4 overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-800 dark:text-white">
              {activeTab === 'ongoing' ? 'In progress' : 'History'}
            </h2>
            
          </div>
          
          <div>
            {isLoading ? (
                <div className="flex flex-col gap-4">
                    {[1, 2, 3].map(i => <div key={i} className="h-48 bg-gray-200 animate-pulse rounded-lg" />)}
                </div>
            ) : (
              activeTab === 'ongoing' ? (
                filteredOngoingProjects.length > 0 ? (
                  filteredOngoingProjects.map(project => (
                    <OngoingProjectCard key={project._id} project={project} />
                  ))
                ) : <p className="text-center text-gray-400 py-10">No ongoing projects</p>
              ) : (
                filteredHistoryProjects.length > 0 ? (
                  filteredHistoryProjects.map(project => (
                    <HistoryProjectCard key={project._id} project={project} />
                  ))
                ) : <p className="text-center text-gray-400 py-10">No history projects</p>
              )
            )}
          </div>
        </div>

        {/* Right Sidebar - Shows the opposite content */}
        <div className="hidden md:block flex-1 bg-white dark:bg-[#141414] p-6 overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-semibold text-gray-800 dark:text-white uppercase tracking-widest text-xs">
              {activeTab === 'ongoing' ? 'Project History' : 'In progress'}
            </h2>
            <div className="flex items-center gap-2">
              <select 
                value={historyStatusFilter}
                onChange={(e) => setHistoryStatusFilter(e.target.value)}
                className="text-[11px] font-medium bg-gray-50 dark:bg-[#1d2026] border border-gray-200 dark:border-[#32353c] rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#174568] text-gray-600 dark:text-gray-300 appearance-none cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                style={{ backgroundImage: 'none' }} // Remove default arrow for cleaner look
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="disputed">Disputed</option>
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {isLoading ? (
                <div className="col-span-full h-48 bg-gray-50 animate-pulse rounded-lg" />
            ) : (
              activeTab === 'ongoing' ? (
                filteredHistoryProjects.map(project => (
                  <HistoryProjectCard key={project._id} project={project} />
                ))
              ) : (
                filteredOngoingProjects.map(project => (
                  <OngoingProjectCard key={project._id} project={project} />
                ))
              )
            )}
          </div>
        </div>
      </div>

      {showCountdownModal && (
        <CountdownModal 
            project={showCountdownModal} 
            onClose={() => setShowCountdownModal(null)} 
        />
      )}

      {showProjectDetails && (
        <ProjectDetailsModal 
            project={showProjectDetails} 
            onClose={() => setShowProjectDetails(null)} 
        />
      )}
    </div>
  );
};

export default Overview;